import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useTexts, useSiteInfo } from '../../hooks/useTexts';

export default function Footer() {
  const { getText } = useTexts();
  const { getSiteInfo } = useSiteInfo();
  const resolveLabel = (key: string, fallback: string) => {
    const value = getText(key);
    return value === key ? fallback : value;
  };
  
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            {(() => {
              const logoInfo = getSiteInfo('footer.company_logo');
              const logoUrl = logoInfo?.image_url_final;
              const companyName = getText('header.company_name');
              
              if (logoUrl) {
                return (
                  <>
                    <img 
                      src={import.meta.env.VITE_API_BASE_URL?.replace('/api', '') + logoUrl} 
                      alt={companyName}
                      className="h-16 w-auto object-contain"
                    />
                  </>
                );
              }
              return <h3 className="text-2xl font-bold text-blue-400">{companyName}</h3>;
            })()}
            <p className="text-gray-300 text-sm leading-relaxed">
              {getText('footer.company_description')}
            </p>
            {/* <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
            </div> */}
          </div>

          {/* Navigation Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">{getText('footer.navigation')}</h4>
            <nav className="space-y-2">
              <Link to="/" className="block text-gray-300 hover:text-white transition-colors">
                {resolveLabel('nav.home', 'Accueil')}
              </Link>
              <Link to="/projects" className="block text-gray-300 hover:text-white transition-colors">
                {resolveLabel('nav.projects', 'Projets')}
              </Link>
              <Link to="/news" className="block text-gray-300 hover:text-white transition-colors">
                {resolveLabel('nav.news', 'Actualités')}
              </Link>
              <Link to="/about" className="block text-gray-300 hover:text-white transition-colors">
                {resolveLabel('nav.about', 'À propos')}
              </Link>
              <Link to="/contact" className="block text-gray-300 hover:text-white transition-colors">
                {resolveLabel('nav.contact', 'Contact')}
              </Link>
            </nav>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">{getText('footer.services')}</h4>
            <div className="space-y-2 text-gray-300">
              <p>{getText('footer.service_apartments')}</p>
              <p>{getText('footer.service_villas')}</p>
              <p>{getText('footer.service_commercial')}</p>
              <p>{getText('footer.service_financing')}</p>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">{getText('footer.contact_us')}</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-gray-300 text-sm">
                  <p>{getText('contact.info.address_value')}</p>
                  <p>{getText('contact.info.city_value')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div className="text-gray-300 text-sm">
                  <p>{getText('contact.info.phone_value')}</p>
                  <p>{getText('contact.info.phone_value_2')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div className="text-gray-300 text-sm">
                  <a href={`mailto:${getText('contact.info.email_value')}`} 
                     className="text-gray-300 hover:text-white transition-colors">
                    {getText('contact.info.email_value')}
                  </a>
                  <br />
                  <a href={`mailto:${getText('contact.info.email_value_2')}`} 
                     className="text-gray-300 hover:text-white transition-colors">
                    {getText('contact.info.email_value_2')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <p className="text-gray-400 text-sm text-center">
              {getText('footer.copyright').replace('2024', new Date().getFullYear().toString())}
            </p>
            {/* <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                {getText('footer.terms')}
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                {getText('footer.privacy')}
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                {getText('footer.legal')}
              </a>
            </div> */}
          </div>
      </div>
    </footer>
  );
}